import type { ReactNode } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import NestableClickableListItem from 'LuxKit/List/NestableClickableListItem'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Divider from 'LuxKit/Divider'
import BookedLabel from 'tripPlanner/components/BookedLabel'

const Row = styled(NestableClickableListItem)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  &.size-medium { padding: ${rem(16)}; }
  gap: ${rem(16)};
  min-width: 0;
  border-radius: ${props => props.theme.borderRadius.M};
`

const Thumbnail = styled.div`
  width: ${rem(64)};
  height: ${rem(64)};
  flex-shrink: 0;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.M};
`

const Actions = styled(Group)<{ $hasPrice: boolean }>`
  flex-shrink: 0;
  width: ${props => props.$hasPrice ? rem(180) : 'auto'};
`

interface Props {
  name: string
  imageId?: string
  description?: string
  rating?: number
  dates?: string | null
  checkInTime?: string | null
  booked: boolean
  price?: ReactNode
  bundleLabel?: ReactNode
  actions: ReactNode
  onClick: () => void
}

function AccommodationItemRow({ name, imageId, description, rating, dates, checkInTime, booked, price, bundleLabel, actions, onClick }: Props) {
  const hasRating = rating !== undefined && rating > 0
  return (
    <Row
      size="medium"
      title={name}
      titleWeight="bold"
      titleLineClamp={1}
      onClick={onClick}
      startIcon={imageId ? <Thumbnail><ResponsiveImage id={imageId} alt={name} aspectRatio="1:1" fit="center" /></Thumbnail> : undefined}
      subtitle={
        <Group direction="vertical" gap={4}>
          {bundleLabel}
          <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap" noDisplayWhenEmpty>
            {hasRating && <NumberRating rating={rating} variant="xs" hideSubtitle inlineLabel />}
            {hasRating && dates && <Divider kind="secondary" direction="vertical" />}
            {dates && <Caption variant="medium" colour="light-foreground-alt">{dates}</Caption>}
            {(hasRating || dates) && checkInTime && <Divider kind="secondary" direction="vertical" />}
            {checkInTime && <Caption variant="medium" colour="light-foreground-alt">{checkInTime}</Caption>}
          </Group>
          {description && <Caption variant="medium" colour="light-foreground-alt">{description}</Caption>}
        </Group>
      }
      endIcon={actions}
    >
      <Actions $hasPrice={!booked && !!price} direction="vertical" gap={4} verticalAlign="center" horizontalAlign={booked ? 'end' : 'start'} noDisplayWhenEmpty>
        {booked && <BookedLabel />}
        {price}
      </Actions>
    </Row>
  )
}

export default AccommodationItemRow
